import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { produce } from 'immer';
import _ from 'lodash';
import moment from 'moment';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useMount from '../../../../../common/hooks/useMount';
import Dropdown from '../../../../../common/widgets/Dropdown';
import { CustomerInfoContext } from '../../../contexts/CustomerInfoContext';
import { StatisticsFilterModel } from '../../../models/StatisticsFilterModel';
import { resources } from '../../../resources/resources';
import { companyService } from '../../../services/company';
import { BudgetTimeline } from './Components/BudgetTimeline';
import ClientOrderProducts from './Components/ClientOrderProducts';
import { ClientOrdersStatistics } from './Components/ClientOrdersStatistics';
import { OrdersTimeline } from './Components/OrdersTimeline';
import { getDateIntervalValue } from './helper';
import { IntervalValueTypes } from './types';

export interface GeneralStatisticsState {
  selectedInterval: { id: number; value: string };
  selectedCompany: { id: number; value: string };
  filters: StatisticsFilterModel;
  otherData: {
    companies: { id: number; value: string }[];
  };
}

export default function GeneralStatisticsPage() {
  const { t: translate } = useTranslation();
  const { customerInfo } = useContext(CustomerInfoContext);

  const companiesDefaultItem = {
    id: 0,
    value: translate(resources.admin.statistics.filters.companyDefault),
  };
  const dateIntervalItems: { id: number; value: string }[] = [
    {
      id: IntervalValueTypes.Last30Days,
      value: translate(resources.admin.statistics.filters.last30Days),
    },
    {
      id: IntervalValueTypes.Last90Days,
      value: translate(resources.admin.statistics.filters.last90Days),
    },
    {
      id: IntervalValueTypes.PastMonth,
      value: translate(resources.admin.statistics.filters.pastMonth),
    },
    {
      id: IntervalValueTypes.Last12Month,
      value: translate(resources.admin.statistics.filters.last12Month),
    },
    {
      id: IntervalValueTypes.PastYear,
      value: translate(resources.admin.statistics.filters.pastYear),
    },
    {
      id: IntervalValueTypes.ThisYear,
      value: translate(resources.admin.statistics.filters.thisYear),
    },
    {
      id: IntervalValueTypes.Custom,
      value: translate(resources.all),
    },
  ];

  const initialState: GeneralStatisticsState = {
    selectedInterval: dateIntervalItems[0],
    selectedCompany: companiesDefaultItem,
    filters: {
      dateStart: moment().subtract(30, 'days').toDate(),
      dateEnd: moment().toDate(),
      companyId: undefined,
      clientId: customerInfo.clientId,
    },
    otherData: {
      companies: [],
    },
  };
  const [state, setState] = useState<GeneralStatisticsState>(initialState);

  const loadOtherData = async (
    abortController: AbortController | undefined,
  ) => {
    const result = await companyService.getCompaniesByClient(
      '?$select=CompanyId, Name',
      {
        signal: abortController?.signal,
      },
    );

    if (!result.hasErrors) {
      setState(
        produce(d => {
          d.otherData.companies = result.data.map(c => {
            return { id: c.companyId, value: c.name };
          });
        }),
      );
    }
  };

  const handleDateIntervalChanged = (e: DropDownListChangeEvent) => {
    const interval = getDateIntervalValue(e.value.id);
    setState(
      produce(d => {
        d.selectedInterval = e.value;
        d.filters.dateStart = interval.dateStart;
        d.filters.dateEnd = interval.dateEnd;
      }),
    );
  };

  const handleCompanyChanged = (e: DropDownListChangeEvent) => {
    if (e.value.id === companiesDefaultItem.id) {
      setState(
        produce(d => {
          d.selectedCompany = e.value;
          d.filters.clientId = customerInfo.clientId;
          d.filters.companyId = undefined;
        }),
      );
    } else {
      setState(
        produce(d => {
          d.selectedCompany = e.value;
          d.filters.clientId = undefined;
          d.filters.companyId = e.value.id;
        }),
      );
    }
  };

  useMount(loadOtherData);

  return (
    <>
      <div className="panel padding-xl admin-statistics-section section-top">
        <h1>{translate(resources.admin.statistics.title)}</h1>
        <div className="inline-form">
          <div className="inline-form-fields">
            <Dropdown
              label={translate(resources.admin.statistics.filters.periodLabel)}
              data={dateIntervalItems}
              dataItemKey="id"
              textField="value"
              value={state.selectedInterval}
              onChange={handleDateIntervalChanged}
              className="statistics-period-selector"
            />
            <Dropdown
              data={_.orderBy(
                [...state.otherData.companies, companiesDefaultItem],
                ['id', 'asc'],
              )}
              dataItemKey="id"
              textField="value"
              value={state.selectedCompany}
              onChange={handleCompanyChanged}
            />
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col col-6 col-lg-6 col-md-6 col-sm-12">
          <BudgetTimeline filters={state.filters} />
        </div>
        <div className="col col-6 col-lg-6 col-md-6 col-sm-12">
          <OrdersTimeline filters={state.filters} />
        </div>
      </div>
      <ClientOrdersStatistics filters={state.filters} />
      <ClientOrderProducts filters={state.filters} />
    </>
  );
}
